import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'

function classNames(...values: Array<string | undefined | false>): string {
  return values.filter(Boolean).join(' ')
}

export interface NavigationItem {
  id: string
  label: string
  href: string
  icon?: ReactNode
}

export interface AppShellProps {
  brand: string
  brandNote?: string
  brandMark?: ReactNode
  navigation: readonly NavigationItem[]
  currentNavigationId: string
  actions?: ReactNode
  children: ReactNode
  onNavigate?: (item: NavigationItem) => void
}

export function AppShell({
  brand,
  brandNote,
  brandMark,
  navigation,
  currentNavigationId,
  actions,
  children,
  onNavigate,
}: AppShellProps) {
  return (
    <div className="riri-app-shell">
      <a className="riri-skip-link" href="#main-content">
        Skip to content
      </a>
      <header className="riri-app-header">
        <div className="riri-brand-lockup">
          {brandMark ? <span className="riri-brand-mark" aria-hidden="true">{brandMark}</span> : null}
          <span className="riri-brand-copy">
            <strong>{brand}</strong>
            {brandNote ? <small>{brandNote}</small> : null}
          </span>
        </div>
        {actions ? <div className="riri-header-actions">{actions}</div> : null}
        <nav className="riri-top-tabs" aria-label="Primary navigation">
          {navigation.map((item) => {
            const current = item.id === currentNavigationId
            return (
              <a
                className={classNames('riri-top-tab', current && 'riri-top-tab-current')}
                href={item.href}
                aria-current={current ? 'page' : undefined}
                key={item.id}
                onClick={onNavigate ? (event) => {
                  event.preventDefault()
                  onNavigate(item)
                } : undefined}
              >
                {item.icon ? <span className="riri-top-tab-icon" aria-hidden="true">{item.icon}</span> : null}
                <span>{item.label}</span>
              </a>
            )
          })}
        </nav>
      </header>
      <main className="riri-app-main" id="main-content" tabIndex={-1}>
        {children}
      </main>
    </div>
  )
}

export interface PageHeaderProps {
  eyebrow?: string
  title: string
  description?: string
  actions?: ReactNode
}

export function PageHeader({ eyebrow, title, description, actions }: PageHeaderProps) {
  return (
    <header className="riri-page-header riri-enter">
      <div className="riri-page-copy">
        {eyebrow ? <p className="riri-eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="riri-page-actions">{actions}</div> : null}
    </header>
  )
}

export type ButtonTone = 'primary' | 'secondary' | 'quiet' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ButtonTone
}

export function Button({ tone = 'primary', className, type = 'button', ...props }: ButtonProps) {
  return (
    <button
      className={classNames('riri-button', `riri-button-${tone}`, className)}
      type={type}
      {...props}
    />
  )
}

export function Card({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <section className={classNames('riri-card', className)} {...props} />
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames('riri-card-header', className)} {...props} />
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames('riri-card-body', className)} {...props} />
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames('riri-card-footer', className)} {...props} />
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string
  description?: string
  error?: string
}

export function TextField({
  label,
  description,
  error,
  className,
  id: providedId,
  ...inputProps
}: TextFieldProps) {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const descriptionId = description ? `${id}-description` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [inputProps['aria-describedby'], descriptionId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <label className="riri-field" htmlFor={id}>
      <span className="riri-field-label">{label}</span>
      <input
        {...inputProps}
        className={classNames('riri-input', className)}
        id={id}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
      />
      {description ? <span className="riri-field-description" id={descriptionId}>{description}</span> : null}
      {error ? <span className="riri-field-error" id={errorId}>{error}</span> : null}
    </label>
  )
}

export type StatusTone = 'neutral' | 'ready' | 'warning' | 'error' | 'special'

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone
}

export function StatusPill({ tone = 'neutral', className, ...props }: StatusPillProps) {
  return <span className={classNames('riri-status-pill', `riri-status-${tone}`, className)} {...props} />
}

export interface NoticeProps extends HTMLAttributes<HTMLElement> {
  tone?: 'info' | 'success' | 'warning' | 'error'
  title: string
  icon?: ReactNode
}

export function Notice({ tone = 'info', title, icon, children, className, ...props }: NoticeProps) {
  return (
    <aside
      className={classNames('riri-notice', `riri-notice-${tone}`, className)}
      role={tone === 'error' ? 'alert' : undefined}
      {...props}
    >
      {icon ? <span className="riri-notice-icon" aria-hidden="true">{icon}</span> : null}
      <div>
        <strong>{title}</strong>
        {children ? <div className="riri-notice-copy">{children}</div> : null}
      </div>
    </aside>
  )
}

export interface EmptyStateProps extends HTMLAttributes<HTMLElement> {
  icon?: ReactNode
  title: string
  action?: ReactNode
}

export function EmptyState({ icon, title, action, children, className, ...props }: EmptyStateProps) {
  return (
    <section className={classNames('riri-empty-state', className)} {...props}>
      {icon ? <span className="riri-empty-icon" aria-hidden="true">{icon}</span> : null}
      <h2>{title}</h2>
      {children ? <div className="riri-empty-copy">{children}</div> : null}
      {action ? <div className="riri-empty-action">{action}</div> : null}
    </section>
  )
}

export function List({ className, ...props }: HTMLAttributes<HTMLUListElement>) {
  return <ul className={classNames('riri-list', className)} {...props} />
}

export interface ListRowProps extends HTMLAttributes<HTMLLIElement> {
  title: string
  description?: string
  leading?: ReactNode
  trailing?: ReactNode
}

export function ListRow({ title, description, leading, trailing, className, ...props }: ListRowProps) {
  return (
    <li className={classNames('riri-list-row', className)} {...props}>
      {leading ? <span className="riri-list-leading" aria-hidden="true">{leading}</span> : null}
      <span className="riri-list-copy">
        <strong>{title}</strong>
        {description ? <small>{description}</small> : null}
      </span>
      {trailing ? <span className="riri-list-trailing">{trailing}</span> : null}
    </li>
  )
}

export interface CatalogPreviewCardProps extends HTMLAttributes<HTMLElement> {
  name: string
  summary: string
  version: string
  icon?: ReactNode
  status?: ReactNode
}

export function CatalogPreviewCard({
  name,
  summary,
  version,
  icon,
  status,
  children,
  className,
  ...props
}: CatalogPreviewCardProps) {
  return (
    <article className={classNames('riri-catalog-card', 'riri-enter', className)} {...props}>
      <div className="riri-catalog-heading">
        {icon ? <span className="riri-catalog-icon" aria-hidden="true">{icon}</span> : null}
        <div>
          <h2>{name}</h2>
          <span className="riri-version">v{version}</span>
        </div>
        {status ? <div className="riri-catalog-status">{status}</div> : null}
      </div>
      <p>{summary}</p>
      {children ? <div className="riri-catalog-content">{children}</div> : null}
    </article>
  )
}

export interface SheetProps {
  open: boolean
  title: string
  description?: string
  children: ReactNode
  onOpenChange: (open: boolean) => void
}

export function Sheet({ open, title, description, children, onOpenChange }: SheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      className="riri-sheet"
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onCancel={(event) => {
        event.preventDefault()
        onOpenChange(false)
      }}
      onClose={() => onOpenChange(false)}
    >
      <div className="riri-sheet-handle" aria-hidden="true" />
      <header className="riri-sheet-header">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description ? <p id={descriptionId}>{description}</p> : null}
        </div>
        <Button tone="quiet" onClick={() => onOpenChange(false)}>Close</Button>
      </header>
      <div className="riri-sheet-body">{children}</div>
    </dialog>
  )
}

export function SheetActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames('riri-sheet-actions', className)} {...props} />
}
